export const useBlogData = () => {
  const { sanitize } = useUtils();

  const transformBlogHome = (response) => {
    const dataResponse = response?.data || {};
    const attributes = dataResponse?.attributes || {};
    const data = {
      id: dataResponse?.id ?? null,
      title: attributes.title,
      body: attributes.body,
      blocks: sanitize((attributes.blocks || []).map((element) => {
        const result = {
          ...element,
          component: element.__component,
        }
        delete result.__component;
        return result;
      })),
      noResultsAvailable: attributes.noResultsAvailable,
      jumpToTop: attributes.jumpToTop,
      seo: sanitize(attributes.seo)
    }
    return data;
  }

  const transformBlogSettings = (response) => {
    const dataResponse = response?.data || {};
    const attributes = dataResponse?.attributes || {};
    const data = {
      id: dataResponse?.id ?? null,
      featuredPosts: Array.isArray(attributes?.featuredPosts?.data) ? attributes.featuredPosts.data : [],
      categories: Array.isArray(attributes?.blogCategories?.data) ? attributes.blogCategories.data : [],
    }
    return data;
  }

  const transformPosts = (response) => {
    const data = (response?.data || []).map((element) => {
      const attributes = element?.attributes || {};
      let date = Date.parse(attributes.publishedAt)
      let year = new Intl.DateTimeFormat('en', { year: 'numeric' }).format(date);
      let month = new Intl.DateTimeFormat('en', { month: 'long' }).format(date);
      let day = new Intl.DateTimeFormat('en', { day: '2-digit' }).format(date);
      return {
        id: element?.id ?? null,
        author: attributes?.author?.data ? attributes.author.data.attributes : null,
        // blocks: element.attributes.blocks,
        // blocks_bottom: element.attributes.blocks_bottom,
        categories: Array.isArray(attributes?.categories?.data) ? attributes.categories.data : [],
        image: attributes?.image?.data ? attributes.image.data.attributes : attributes?.boltImage ? attributes.boltImage : null,
        publishedAt: attributes.publishedAt ? `${month} ${day}, ${year}` : null,
        publishedAtRaw: attributes.publishedAt,
        // relatedPosts: element.attributes.relatedPosts.data,
        shortDescription: attributes.shortDescription,
        slug: attributes.slug,
        title: attributes.title,
        // updatedAt: element.attributes.updatedAt,
      }
    })
    return {
      data: data,
      meta: response.meta
    }
  }

  const transformPost = (response) => {
    const dataResponse = response?.data || {};
    const attributes = dataResponse?.attributes || {};
    let date = Date.parse(attributes.publishedAt)
    let year = new Intl.DateTimeFormat('en', { year: 'numeric' }).format(date);
    let month = new Intl.DateTimeFormat('en', { month: 'long' }).format(date);
    let day = new Intl.DateTimeFormat('en', { day: '2-digit' }).format(date);

    let publicationDate = Date.parse(`${attributes.publicationDate}T00:00:00`);
    let publicationYear = !isNaN(publicationDate)
    ? new Intl.DateTimeFormat('en', { year: 'numeric' }).format(publicationDate)
    : null;
    let publicationMonth = !isNaN(publicationDate)
      ? new Intl.DateTimeFormat('en', { month: 'long' }).format(publicationDate)
      : null;
    let publicationDay = !isNaN(publicationDate)
      ? new Intl.DateTimeFormat('en', { day: '2-digit' }).format(publicationDate)
      : null;
    
    const data = {
      ...attributes,
      id: dataResponse?.id ?? null,
      title: attributes.title,
      image: attributes?.image?.data ? attributes.image.data.attributes : null,
      shortDescription: attributes.shortDescription,
      author: attributes?.author?.data ? attributes.author.data.attributes : null,
      categories: attributes.categories,
      relatedPosts: Array.isArray(attributes?.relatedPosts?.data) ? attributes.relatedPosts.data : [],
      publishedAt: attributes.publishedAt ? `${month} ${day}, ${year}` : null,
      publishedAtRaw: attributes.publishedAt,
      publicationDate: publicationYear && publicationMonth && publicationDay
      ? `${publicationMonth} ${publicationDay}, ${publicationYear}`
      : null,
      blocks: sanitize((attributes.blocks || []).map((element) => {
        const result = {
          ...element,
          component: element.__component,
        }
        delete result.__component;
        return result;
      })),
      blocks_bottom: sanitize((attributes.blocks_bottom || []).map((element) => {
        const result = {
          ...element,
          component: element.__component,
        }
        delete result.__component;
        return result;
      })),
      seo: sanitize(attributes.seo),
    }
    return data;
  }

  const transformAuthor = (response) => {
    let author = (response?.data || [])[0]?.attributes || {}
    const data = {
      id: (response?.data || [])[0]?.id ?? null,
      name: author.name,
      image: author?.image?.data?.attributes ?? { url: null },
      background: author.background,
      linkedIn: author.linkedInUrl,
      slug: author.slug
    }
    return data;
  }

  const transformCategories = (response) => {
    const data = (response?.data || []).map((element) => {
      return {
        id: element?.id ?? null,
        category: element?.category,
        slug: element?.slug
      }
    })
    return {
      data: data,
      meta: response.meta
    }
  }

  const transformCategory = (response) => {
    let category = (response?.data || [])[0]?.attributes || {}
    const data = {
      id: (response?.data || [])[0]?.id ?? null,
      category: category.category,
      slug: category.slug
    }
    return data;
  }

  const transformLeadGen = (response) => {
    const attributes = response?.data?.attributes || {};
    const result = {
      ...attributes
    }
    return result;
  }

  const getBlogHome = async () => {
    const url = `/api/blog`;

    return await useFetch(url, {
      method: 'GET',
      transform: transformBlogHome
    });
  }

  const getBlogSettings = async () => {
    const url = `/api/blog-setting`;

    return await useFetch(url, {
      method: 'GET',
      transform: transformBlogSettings
    });
  }

  const getPosts = async (paramsString = '?sort=publishedAt:DESC&_start=0') => {
    const url = `/api/posts${paramsString}`;

    return await useFetch(url, {
      method: 'GET',
      transform: transformPosts
    });
  }

  const getPostBySlug = async (slug) => {
    const { findOneBySlug } = useStrapiAsyncData();
    const url = 'posts';
    const params = {
      slug: slug,
      entity: url
    }
    return findOneBySlug(url, params, transformPost);
  }

  const getAuthorBySlug = async (slug) => {
    const url = `/api/blog-author?slug=${slug}`;

    return await useFetch(url, {
      method: 'GET',
      transform: transformAuthor
    });
  }

  const getPrevPost = async (date) => {
    const url = `/api/posts-prev?date=${date}`

    return await useFetch(url, {
      method: 'GET',
      transform: transformPosts,
    })
  }

  const getCategoryBySlug = async (slug) => {
    const url = `/api/blog-categories?slug=${slug}`;

    return await useFetch(url, {
      method: 'GET',
      transform: transformCategory
    });
  }

  const getBlogCategories = async () => {
    const url = `/api/blog-category?sort=publishedAt:DESC&_start=0`;

    return await useFetch(url, {
      method: 'GET',
      transform: transformCategories
    });
  }

  const getNextPost = async (date) => {
    const url = `/api/posts-next?date=${date}`

    return await useFetch(url, {
      method: 'GET',
      transform: transformPosts,
    })
  }

  const blogLeadGen = async (_deep = true) => {
    const url = `/api/blog-floating-block-lead-gen`

    let res = await useFetch(url, {
      method: 'GET',
      transform: transformLeadGen
    })
    return res.data
  }

  return {
    getBlogSettings,
    getBlogHome,
    getPosts,
    getPostBySlug,
    getAuthorBySlug,
    getPrevPost,
    getNextPost,
    getBlogCategories,
    getCategoryBySlug,
    blogLeadGen
  }
}

